import type { AnalyticsExportFormat } from '../components/analytics/AnalyticsExportMenu';
import type {
  PeerReviewAnalyticsSummary,
  PeerReviewModalityRow,
  PeerReviewQualityTrendPoint,
} from '../services/peerReviewAnalyticsService';
import { openAnalyticsPrintWindow } from './analyticsExport';
import { formatDisplayDate, formatGeneratedAt } from './billingReconciliationExport';

export interface PeerReviewExportMeta {
  startDate: string;
  endDate: string;
  modalityLabel?: string;
  radiologistLabel?: string;
  remarkLabel?: string;
  priorityLabel?: string;
  bodyPartLabel?: string;
  trendPeriodLabel?: string;
}

export interface PeerReviewExportData {
  summary: PeerReviewAnalyticsSummary;
  trend: PeerReviewQualityTrendPoint[];
  modalityRows: PeerReviewModalityRow[];
}

function escapeCsv(value: string | number): string {
  return `"${String(value).replaceAll('"', '""')}"`;
}

function escapeHtml(value: string | number): string {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function formatPeriodLabel(startDate: string, endDate: string): string {
  const start = formatDisplayDate(startDate);
  const end = formatDisplayDate(endDate);
  return startDate === endDate ? start : `${start} – ${end}`;
}

function formatPercent(value: number): string {
  if (!Number.isFinite(value)) {
    return '0%';
  }
  return `${value.toFixed(1)}%`;
}

function downloadBlob(filename: string, content: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

function buildModalityTableRows(rows: PeerReviewModalityRow[]): string {
  if (!rows.length) {
    return '<tr><td colspan="8">No data for the selected filters.</td></tr>';
  }
  return rows
    .map(
      row =>
        `<tr>
          <td>${escapeHtml(row.modality)}</td>
          <td>${escapeHtml(row.reviews)}</td>
          <td>${escapeHtml(row.pending)}</td>
          <td>${escapeHtml(row.concur)}</td>
          <td>${escapeHtml(row.minorDiscrepancy)}</td>
          <td>${escapeHtml(row.clinicallySignificantDiscrepancy)}</td>
          <td>${escapeHtml(row.correctiveActions)}</td>
          <td>${escapeHtml(row.avgReviewTatFormatted)}</td>
        </tr>`
    )
    .join('');
}

function buildTrendTableRows(points: PeerReviewQualityTrendPoint[]): string {
  if (!points.length) {
    return '<tr><td colspan="4">No trend data for the selected filters.</td></tr>';
  }
  return points
    .map(
      point =>
        `<tr>
          <td>${escapeHtml(point.label)}</td>
          <td>${escapeHtml(point.concur)}</td>
          <td>${escapeHtml(point.minorDiscrepancy)}</td>
          <td>${escapeHtml(point.clinicallySignificantDiscrepancy)}</td>
        </tr>`
    )
    .join('');
}

function buildReportHtml(data: PeerReviewExportData, meta: PeerReviewExportMeta): string {
  const { summary, trend, modalityRows } = data;
  const filterRows = [
    ['Report period', formatPeriodLabel(meta.startDate, meta.endDate)],
    meta.modalityLabel ? ['Modality', meta.modalityLabel] : null,
    meta.radiologistLabel ? ['Radiologist', meta.radiologistLabel] : null,
    meta.remarkLabel ? ['Remark', meta.remarkLabel] : null,
    meta.priorityLabel ? ['Priority', meta.priorityLabel] : null,
    meta.bodyPartLabel ? ['Body part', meta.bodyPartLabel] : null,
    meta.trendPeriodLabel ? ['Trend period', meta.trendPeriodLabel] : null,
    ['Generated on', formatGeneratedAt()],
  ]
    .filter((row): row is [string, string] => row !== null)
    .map(([label, value]) => `<tr><td>${escapeHtml(label)}</td><td>${escapeHtml(value)}</td></tr>`)
    .join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Peer Review Analytics</title>
  <style>
    body { font-family: 'Segoe UI', Arial, sans-serif; color: #0f172a; margin: 0; padding: 24px; background: #f8fafc; }
    .report { max-width: 960px; margin: 0 auto; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; }
    .report-header { background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); color: #fff; padding: 24px 28px; }
    .report-header h1 { margin: 0 0 8px; font-size: 24px; font-weight: 700; }
    .report-body { padding: 24px 28px 32px; }
    .summary-grid { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 12px; margin-bottom: 24px; }
    .summary-card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; background: #f8fafc; }
    .summary-card h3 { margin: 0 0 8px; font-size: 13px; color: #64748b; font-weight: 500; }
    .summary-card p { margin: 0; font-size: 24px; font-weight: 700; }
    h2 { font-size: 16px; margin: 24px 0 12px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th, td { border: 1px solid #e2e8f0; padding: 8px; text-align: left; }
    th { background: #f8fafc; }
  </style>
</head>
<body>
  <div class="report">
    <div class="report-header">
      <h1>Peer Review Analytics</h1>
      <p>Quality metrics and modality breakdown</p>
    </div>
    <div class="report-body">
      <h2>Filters</h2>
      <table><tbody>${filterRows}</tbody></table>
      <div class="summary-grid">
        <div class="summary-card"><h3>Total peer reviews</h3><p>${escapeHtml(summary.totalPeerReviews)}</p></div>
        <div class="summary-card"><h3>Agreement rate</h3><p>${escapeHtml(formatPercent(summary.agreementRatePercent))}</p></div>
        <div class="summary-card"><h3>Discrepancy rate</h3><p>${escapeHtml(formatPercent(summary.discrepancyRatePercent))}</p></div>
        <div class="summary-card"><h3>Pending peer reviews</h3><p>${escapeHtml(summary.pendingPeerReviews)}</p></div>
        <div class="summary-card"><h3>Avg review completion time</h3><p>${escapeHtml(summary.avgReviewCompletionTimeFormatted)}</p></div>
      </div>
      <h2>Peer review quality trend</h2>
      <table>
        <thead>
          <tr>
            <th>Period</th>
            <th>Concur</th>
            <th>Minor discrepancy</th>
            <th>Clinically significant discrepancy</th>
          </tr>
        </thead>
        <tbody>${buildTrendTableRows(trend)}</tbody>
      </table>
      <h2>Peer review summary by modality</h2>
      <table>
        <thead>
          <tr>
            <th>Modality</th>
            <th>Reviews</th>
            <th>Pending</th>
            <th>Concur</th>
            <th>Minor discrepancy</th>
            <th>Clinically significant discrepancy</th>
            <th>Corrective actions</th>
            <th>Avg review TAT</th>
          </tr>
        </thead>
        <tbody>${buildModalityTableRows(modalityRows)}</tbody>
      </table>
    </div>
  </div>
</body>
</html>`;
}

function buildPeerReviewCsv(data: PeerReviewExportData, meta: PeerReviewExportMeta): string {
  const { summary, trend, modalityRows } = data;
  const lines: string[] = [];

  lines.push(
    'PEER REVIEW ANALYTICS',
    `Report period,${escapeCsv(formatPeriodLabel(meta.startDate, meta.endDate))}`
  );
  if (meta.modalityLabel) {
    lines.push(`Modality,${escapeCsv(meta.modalityLabel)}`);
  }
  if (meta.radiologistLabel) {
    lines.push(`Radiologist,${escapeCsv(meta.radiologistLabel)}`);
  }
  if (meta.remarkLabel) {
    lines.push(`Remark,${escapeCsv(meta.remarkLabel)}`);
  }
  if (meta.priorityLabel) {
    lines.push(`Priority,${escapeCsv(meta.priorityLabel)}`);
  }
  if (meta.bodyPartLabel) {
    lines.push(`Body part,${escapeCsv(meta.bodyPartLabel)}`);
  }
  if (meta.trendPeriodLabel) {
    lines.push(`Trend period,${escapeCsv(meta.trendPeriodLabel)}`);
  }

  lines.push(
    '',
    'SUMMARY',
    'Metric,Value',
    `Total peer reviews,${summary.totalPeerReviews}`,
    `Agreement rate,${escapeCsv(formatPercent(summary.agreementRatePercent))}`,
    `Discrepancy rate,${escapeCsv(formatPercent(summary.discrepancyRatePercent))}`,
    `Pending peer reviews,${summary.pendingPeerReviews}`,
    `Avg review completion time,${escapeCsv(summary.avgReviewCompletionTimeFormatted)}`,
    '',
    'PEER REVIEW QUALITY TREND',
    'Period,Concur,Minor discrepancy,Clinically significant discrepancy'
  );
  if (trend.length) {
    trend.forEach(point => {
      lines.push(
        [
          escapeCsv(point.label),
          point.concur,
          point.minorDiscrepancy,
          point.clinicallySignificantDiscrepancy,
        ].join(',')
      );
    });
  } else {
    lines.push('No trend data,,,');
  }

  lines.push(
    '',
    'MODALITY BREAKDOWN',
    'Modality,Reviews,Pending,Concur,Minor discrepancy,Clinically significant discrepancy,Corrective actions,Avg review TAT'
  );
  if (modalityRows.length) {
    modalityRows.forEach(row => {
      lines.push(
        [
          escapeCsv(row.modality),
          row.reviews,
          row.pending,
          row.concur,
          row.minorDiscrepancy,
          row.clinicallySignificantDiscrepancy,
          row.correctiveActions,
          escapeCsv(row.avgReviewTatFormatted),
        ].join(',')
      );
    });
  } else {
    lines.push('No data,,,,,,,');
  }

  return `\uFEFF${lines.join('\r\n')}`;
}

export function exportPeerReviewAnalytics(
  format: AnalyticsExportFormat,
  data: PeerReviewExportData,
  meta: PeerReviewExportMeta
): void {
  const baseName = `peer-review-analytics-${meta.startDate}_${meta.endDate}`;

  if (format === 'csv') {
    downloadBlob(`${baseName}.csv`, buildPeerReviewCsv(data, meta), 'text/csv;charset=utf-8;');
    return;
  }

  if (format === 'excel') {
    const html = buildReportHtml(data, meta).replace(
      '<title>Peer Review Analytics</title>',
      '<title>Peer Review Analytics</title>\n<meta http-equiv="Content-Type" content="application/vnd.ms-excel; charset=UTF-8" />'
    );
    downloadBlob(`${baseName}.xls`, html, 'application/vnd.ms-excel;charset=utf-8;');
    return;
  }

  const summaryHtml = `
    <table><tbody>
      <tr><th>Total peer reviews</th><td>${escapeHtml(data.summary.totalPeerReviews)}</td></tr>
      <tr><th>Agreement rate</th><td>${escapeHtml(formatPercent(data.summary.agreementRatePercent))}</td></tr>
      <tr><th>Discrepancy rate</th><td>${escapeHtml(formatPercent(data.summary.discrepancyRatePercent))}</td></tr>
      <tr><th>Pending peer reviews</th><td>${escapeHtml(data.summary.pendingPeerReviews)}</td></tr>
      <tr><th>Avg review completion time</th><td>${escapeHtml(data.summary.avgReviewCompletionTimeFormatted)}</td></tr>
    </tbody></table>
    <h2>Peer review quality trend</h2>
    <table><thead><tr><th>Period</th><th>Concur</th><th>Minor discrepancy</th><th>Clinically significant discrepancy</th></tr></thead><tbody>${buildTrendTableRows(data.trend)}</tbody></table>
    <h2>Peer review summary by modality</h2>
    <table><thead><tr><th>Modality</th><th>Reviews</th><th>Pending</th><th>Concur</th><th>Minor discrepancy</th><th>Clinically significant discrepancy</th><th>Corrective actions</th><th>Avg review TAT</th></tr></thead><tbody>${buildModalityTableRows(data.modalityRows)}</tbody></table>
  `;
  openAnalyticsPrintWindow('Peer Review Analytics', summaryHtml);
}
